// routeUtils.js
import axios from 'axios';
import polyline from '@mapbox/polyline';

// Server tedesco come primario
const OSRM_API = 'https://routing.openstreetmap.de/routed-foot/route/v1/foot/';
// Server ufficiale come backup
const OSRM_BACKUP_API = 'https://router.project-osrm.org/route/v1/foot/';
const OSRM_TIMEOUT = 10000; // 10s timeout
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000; // 24 ore in millisecondi

// Cache per memorizzare i risultati delle chiamate OSRM
const distanceCache = new Map();
const segmentCache = new Map();
const firstRequestTime = Date.now();
let requestCount = 0;
let lastRequestTime = Date.now();

// Carica la cache dal localStorage se disponibile
try {
  const savedCache = localStorage.getItem('routeDistanceCache');
  if (savedCache) {
    const parsedCache = JSON.parse(savedCache);
    
    if (parsedCache.routes) {
      parsedCache.routes.forEach(([key, value]) => {
        distanceCache.set(key, value);
      });
    }
    
    if (parsedCache.segments) {
      parsedCache.segments.forEach(([key, value]) => {
        segmentCache.set(key, value);
      });
    }
    
    // console.log('Cache route caricata da localStorage', {
    //   routes: distanceCache.size,
    //   segments: segmentCache.size
    // });
  }
} catch (error) {
  console.error('Errore nel caricamento della cache:', error);
}

// Funzione per salvare la cache in localStorage
const saveCache = () => {
  try {
    const cacheData = {
      routes: Array.from(distanceCache.entries()),
      segments: Array.from(segmentCache.entries())
    };
    
    localStorage.setItem('routeDistanceCache', JSON.stringify(cacheData));
    // console.log('Cache route salvata in localStorage');
  } catch (error) {
    console.error('Errore nel salvataggio della cache:', error);
  }
};

// Salva la cache prima di chiudere la pagina
window.addEventListener('beforeunload', saveCache);

// Salva periodicamente
setInterval(saveCache, 5 * 60 * 1000); // Ogni 5 minuti

const generateCacheKey = (pois) => {
  return pois.map(poi => 
    poi.field_geolocalizzazione?.[0]?.lat + ',' + 
    poi.field_geolocalizzazione?.[0]?.lng
  ).join('|');
};

const generateSegmentKey = (start, end) => {
  return `${start[0]},${start[1]}|${end[0]},${end[1]}`;
};

const logOSRMRequest = (requestData) => {
  // console.group('🚀 OSRM Request');
  // console.log('Timestamp:', new Date().toISOString());
  // console.log('URL:', requestData.url);
  // console.log('Coordinates:', requestData.coordinates);
  // console.log('Profile:', requestData.profile);
  // console.groupEnd();
};

const logOSRMResponse = (response, startTime) => {
  const duration = Date.now() - startTime;
  // console.group('✅ OSRM Response');
  // console.log('Duration:', duration + 'ms');
  // console.log('Status:', response.status);
  // console.log('Code:', response.data.code);
  // console.log('Route found:', !!response.data.routes?.length);
  // console.groupEnd();
};

const logOSRMError = (error, startTime) => {
  const duration = Date.now() - startTime;
  // console.group('❌ OSRM Error');
  // console.log('Duration:', duration + 'ms');
  // console.log('Status:', error.response?.status);
  // console.log('Message:', error.message);
  // console.log('Response:', error.response?.data);
  // console.groupEnd();
};

const logCacheStatus = (key, hit, type = 'route') => {
  // console.group('💾 Cache Status');
  // console.log('Type:', type);
  // console.log('Cache Key:', key);
  // console.log('Cache Hit:', hit ? 'YES' : 'NO');
  // console.log('Route Cache Size:', distanceCache.size);
  // console.log('Segment Cache Size:', segmentCache.size);
  // console.groupEnd();
};

const logRateLimit = () => {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;
  requestCount++;
  
  // console.group('⚡ Rate Limit');
  // console.log('Request Count:', requestCount);
  // console.log('Time since last request:', timeSinceLastRequest + 'ms');
  // console.log('Requests per minute:', (requestCount / (now - firstRequestTime) * 60000).toFixed(2));
  // console.groupEnd();
  
  lastRequestTime = now;
};

const decodeGeometry = (geometry) => {
  try {
    const decoded = polyline.decode(geometry);
    // Valida che tutte le coordinate siano numeri validi
    const isValid = decoded.every(coord => 
      Array.isArray(coord) && 
      coord.length === 2 && 
      !isNaN(coord[0]) && 
      !isNaN(coord[1])
    );
    return isValid ? decoded : null;
  } catch (error) {
    console.error('Errore decodifica geometria:', error);
    return null;
  }
};

const fetchWithRetry = async (url, retries = MAX_RETRIES) => {
  try {
    const response = await axios.get(url, { timeout: OSRM_TIMEOUT });
    return response;
  } catch (error) {
    if (retries > 0 && error.code !== 'ECONNABORTED') {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return fetchWithRetry(url, retries - 1);
    }
    throw error;
  }
};

/**
 * Recupera un segmento dalla cache
 * @param {Array} start - Coordinate di partenza [lng, lat]
 * @param {Array} end - Coordinate di arrivo [lng, lat]
 * @returns {Object|null} - Segmento dalla cache o null se non trovato
 */
const getSegmentFromCache = (start, end) => {
  const key = generateSegmentKey(start, end);
  const cachedItem = segmentCache.get(key);
  
  if (!cachedItem) return null;
  
  // Verifica se la cache è scaduta
  if (Date.now() - cachedItem.timestamp > CACHE_EXPIRATION) {
    segmentCache.delete(key);
    return null;
  }
  
  return cachedItem.data;
};

/**
 * Salva un segmento nella cache
 * @param {Array} start - Coordinate di partenza [lng, lat]
 * @param {Array} end - Coordinate di arrivo [lng, lat]
 * @param {Object} data - Dati del segmento da salvare
 */
const saveSegmentToCache = (start, end, data) => {
  const key = generateSegmentKey(start, end);
  segmentCache.set(key, {
    timestamp: Date.now(),
    data
  });
};

/**
 * Calcola la distanza totale della rotta e restituisce i punti del percorso
 * @param {Array} pois - Array di POI con coordinate geografiche
 * @returns {Object} - Oggetto con coordinate e distanze dei segmenti
 */
export const calculateRouteDistance = async (pois) => {
  if (!pois || pois.length < 2) {
    return { coordinates: [], distances: [] };
  }

  try {
    const cacheKey = generateCacheKey(pois);
    const cachedRoute = distanceCache.get(cacheKey);

    if (cachedRoute && (Date.now() - cachedRoute.timestamp < CACHE_EXPIRATION)) {
      logCacheStatus(cacheKey, true, 'route');
      return cachedRoute.data;
    } else if (cachedRoute) {
      distanceCache.delete(cacheKey);
    }

    logCacheStatus(cacheKey, false, 'route');
    
    const coordinates = pois.map(poi => {
      if (!poi.field_geolocalizzazione || !poi.field_geolocalizzazione[0]) {
        throw new Error(`POI "${poi.title?.[0]?.value || 'Sconosciuto'}" non ha coordinate valide`);
      }
      return [poi.field_geolocalizzazione[0].lng, poi.field_geolocalizzazione[0].lat];
    });
    
    const segmentPromises = [];
    const distances = [];
    let totalDistanceMeters = 0;
    const snapSegments = [];
    
    for (let i = 0; i < coordinates.length - 1; i++) {
      const start = coordinates[i];
      const end = coordinates[i + 1];
      
      const cachedSegment = getSegmentFromCache(start, end);
      
      if (cachedSegment) {
        logCacheStatus(generateSegmentKey(start, end), true, 'segment');
        distances[i] = cachedSegment.distance;
        totalDistanceMeters += cachedSegment.distanceMeters;
        segmentPromises.push(Promise.resolve(cachedSegment.coordinates));
      } else {
        logCacheStatus(generateSegmentKey(start, end), false, 'segment');
        
        // Parametri ottimizzati
        const url = `${OSRM_API}${start[0]},${start[1]};${end[0]},${end[1]}?overview=full&geometries=geojson&steps=true&radiuses=500;500&alternatives=false&continue_straight=false&annotations=true`;
        
        segmentPromises.push(
          fetchWithRetry(url)
            .then(response => {
              const data = response.data;
              if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
                throw new Error('Nessun percorso trovato');
              }

              const waypoints = data.waypoints;
              if (waypoints && waypoints[0]) {
                const snappedStart = waypoints[0].location;
                if (start[0] !== snappedStart[0] || start[1] !== snappedStart[1]) {
                  snapSegments.push([
                    [start[1], start[0]],
                    [snappedStart[1], snappedStart[0]]
                  ]);
                }
              }
              if (waypoints && waypoints[1]) {
                const snappedEnd = waypoints[1].location;
                if (end[0] !== snappedEnd[0] || end[1] !== snappedEnd[1]) {
                  snapSegments.push([
                    [end[1], end[0]],
                    [snappedEnd[1], snappedEnd[0]]
                  ]);
                }
              }

              const distanceMeters = data.routes[0].distance;
              const distance = (distanceMeters < 1000)
                ? `${Math.round(distanceMeters)} m`
                : `${(distanceMeters / 1000).toFixed(1)} km`;
              distances[i] = distance;
              totalDistanceMeters += distanceMeters;
              
              saveSegmentToCache(start, end, {
                coordinates: data.routes[0].geometry.coordinates,
                distance,
                distanceMeters
              });
              
              return data.routes[0].geometry.coordinates;
            })
            .catch(error => {
              console.error(`Errore con il server tedesco per il segmento ${i}:`, error);
              // Fallback al server ufficiale
              const backupUrl = `${OSRM_BACKUP_API}${start[0]},${start[1]};${end[0]},${end[1]}?overview=full&geometries=geojson&steps=true&radiuses=500;500&alternatives=false&continue_straight=false&annotations=true`;
              return fetchWithRetry(backupUrl)
                .then(response => {
                  const data = response.data;
                  if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
                    return [start, end];
                  }
                  return data.routes[0].geometry.coordinates;
                })
                .catch(error => {
                  console.error(`Errore anche con il server ufficiale per il segmento ${i}:`, error);
                  return [start, end];
                });
            })
        );
      }
    }

    const segments = await Promise.all(segmentPromises);
    
    let allCoordinates = [];
    segments.forEach((segment, index) => {
      if (allCoordinates.length > 0) {
        allCoordinates = allCoordinates.concat(segment.slice(1));
      } else {
        allCoordinates = segment;
      }
    });
    
    const leafletCoordinates = allCoordinates.map(coord => [coord[1], coord[0]]);
    
    const totalDistanceKm = totalDistanceMeters / 1000;
    const totalDistance = `${totalDistanceKm.toFixed(1)} km`;
    
    const result = {
      coordinates: leafletCoordinates,
      distances,
      totalDistance,
      snapSegments
    };
    
    distanceCache.set(cacheKey, {
      timestamp: Date.now(),
      data: result
    });
    
    return result;
  } catch (error) {
    console.error('Errore durante il calcolo del percorso:', error);
    throw error;
  }
};

/**
 * Formatta una distanza in metri in un formato leggibile
 * @param {number} meters - Distanza in metri
 * @returns {string} - Distanza formattata
 */
export const formatDistance = (meters) => {
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toFixed(1)} km`;
};